/**
 * NOTE:(diwakersurya) This file uses security related checks that are present in redux-api.ts file.
 * and we are just copying the logic to build SSE headers and check authentication for SSE requests.
 */
import type {Dispatch} from 'src/reducers';
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import i18n from 'src/components/lib/i18n/new-i18n';
import {pushModal, popModal} from 'src/action-creators/modal';
import {batch} from 'src/action-creators/batch';
import {setPostAuthPath, setAuthed} from 'src/action-creators/accounts';
import {setMaintenanceMode} from 'src/action-creators/agency';
import {browserHistory} from 'src/reroutes/browser-history';
import {selectCsrfToken} from 'src/selectors/csrf';
import {selectIsAuthed} from 'src/selectors/accounts';


export type BuildSseHeadersOptions = {
  baseHeaders?: {[key: string]: string};
  csrfToken?: string | null | undefined;
  agencySlug?: string | null | undefined;
};

/**
 * NOTE:(diwakersurya) Builds SSE headers. Backend expects these headers to
 * be present in the request headers for all the api calls
 * and we are just copying the logic from redux-api.ts to build these headers.
 */
export function buildSseHeaders(opts: BuildSseHeadersOptions) {
  const {baseHeaders = {}, csrfToken, agencySlug} = opts;
  const headers: {[key: string]: string} = {
    ...baseHeaders,
  };

  if (csrfToken != null) {
    headers['X-Csrf-Token'] = csrfToken || '';
  }
  if (agencySlug) {
    headers['x-agency-slug'] = String(agencySlug);
  }

  return headers;
}

export type HandleAuthAndMaintenanceOptions = {
  dispatch: Dispatch;
  isAuthed: boolean;
  handleAuth?: boolean; // default true
};

export async function handleAuthAndMaintenance(
  res: Response,
  opts: HandleAuthAndMaintenanceOptions,
) {
  const {dispatch, isAuthed, handleAuth = true} = opts;

  // 401 -> trigger login flow if allowed and currently authed
  if (res.status === 401 && handleAuth && isAuthed) {
    // replicate login modal behavior from redux-api.ts
    return dispatch(showLoginModal());
  }

  // 503 maintenance: only set flag when the specific error matches
  if (res.status === 503) {
    try {
      const body = await res.clone().json();
      if (body?.error === 'AgencyUnderMaintenance') {
        dispatch(setMaintenanceMode(true));
      }
    } catch {
      // ignore JSON parse issues; nothing else to do here
    }
  }
}

/**
 * Local copy of the login modal flow from redux-api.ts to avoid importing internals
 */
const showLoginModal = () => (dispatch: Dispatch) =>
  dispatch(
    pushModal({
      type: 'GENERIC_ERROR',
      // @ts-expect-error --force-skip(diwaker)
      title: i18n.t('Expired_Session', 'Expired Session'),
      text: `${i18n.t(
        'Looks_like_your_session_expired_Please_sign_back_in',
        'Looks like your session expired. Please sign back in.',
      )}`,
      confirmText: i18n.t('Okay', 'Okay'),
      handleConfirm: () => {
        const trmPaths = ['/trm', '/trm/'];
        const postAuthPath = trmPaths.includes(window.location.pathname)
          ? `/trm${window.location.hash}`
          : window.location.pathname;
        dispatch(
          batch(popModal(), setAuthed(false), setPostAuthPath(postAuthPath)),
        );
        browserHistory.push('/signin');
      },
      handleAbort: () => {
        dispatch(popModal());
      },
    }),
  );

/**
 * This hook is used to compose headers and check authentication for SSE requests
 */
export function useSseSecurity() {
  const dispatch = useDispatch();
  const csrfToken = useSelector(selectCsrfToken);
  const isAuthed = useSelector(selectIsAuthed);
  const agencySlug = useSelector((s: any) => s.env && s.env.agencySlug);

  const composeHeaders = useCallback(
    (baseHeaders?: {[key: string]: string}) =>
      buildSseHeaders({
        baseHeaders: baseHeaders || {},
        csrfToken,
        agencySlug,
      }),
    [csrfToken, agencySlug],
  );

  const onOpenAuthCheck = useCallback(
    async (res: Response) =>
      handleAuthAndMaintenance(res, {
        dispatch: dispatch as Dispatch,
        isAuthed: Boolean(isAuthed),
        handleAuth: true,
      }),
    [dispatch, isAuthed],
  );

  return {composeHeaders, onOpenAuthCheck};
}
